import { CodeXml, X } from 'lucide-react';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { tokenizeGeneratedSource } from '../source/tokenizeGeneratedSource';

type FlowSourceViewerProps = {
  source: string;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
};

export function FlowSourceViewer({ source, onClose, returnFocusRef }: FlowSourceViewerProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lines = useMemo(() => {
    const withoutTrailingLineBreak = source.endsWith('\n') ? source.slice(0, -1) : source;
    return withoutTrailingLineBreak.split('\n');
  }, [source]);
  const tokenizedLines = useMemo(
    () => lines.map((line) => tokenizeGeneratedSource(line)),
    [lines],
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const returnFocusElement = returnFocusRef?.current;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusElement?.focus();
    };
  }, [onClose, returnFocusRef]);

  return createPortal(
    <div
      className="flow-source-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="生成ソースコード"
    >
      <header className="flow-source-viewer__header">
        <div className="flow-source-viewer__tab" aria-label="表示中のファイル">
          <CodeXml aria-hidden="true" className="flow-source-viewer__tab-icon" />
          <span>flow.generated.js</span>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          className="flow-source-viewer__close"
          aria-label="ソースを閉じる"
          title="ソースを閉じる"
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>
      </header>
      <div className="flow-source-viewer__body" tabIndex={0} aria-label="読み取り専用の生成ソース">
        <div className="flow-source-viewer__code" role="presentation">
          {tokenizedLines.map((tokens, lineIndex) => (
            <div className="flow-source-viewer__line" key={lineIndex}>
              <span className="flow-source-viewer__line-number" aria-hidden="true">
                {lineIndex + 1}
              </span>
              <code className="flow-source-viewer__line-code">
                {tokens.map((token, tokenIndex) => (
                  <span
                    className={`flow-source-token flow-source-token--${token.kind}`}
                    key={`${tokenIndex}-${token.text}`}
                  >
                    {token.text}
                  </span>
                ))}
              </code>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
